import * as Sentry from '@sentry/browser';
import ReactGA from 'react-ga';

import { config } from 'src/config/config';
import { toastNotificationsService, userService } from 'src/config/rootService';
import { RootState } from 'src/config/rootStore';
import { CareRecommendationNode } from 'src/generated/webapp_gql';
import { delay } from 'src/shared/helpers/delay';

import { Role, UserDetails, UserState } from '../models/user';
import { getUserId } from '../selectors/userSelectors';

export const defaultState: UserState = {
    avatar: '',
    company: '',
    companyUuid: '',
    email: '',
    firstName: '',
    birthDate: '',
    id: -1,
    uuid: '',
    lastName: '',
    state: '',
    localTimezone: '',
    username: '',
    zipCode: '',
    mailingAddress: '',
    phoneNumber: '',
    newEmailAddressNotConfirmed: '',
    availability: [],
    city: '',
    country: '',
    parentUuid: null,
    languagePreferences: [],
    mobileAppUsed: false,
    inviteDependentModalVisible: false,
    role: Role.MEMBER,
    defaultVisitDuration: 0,
    companyAdditionalBenefits: [],
    careRecommendations: [],
    eligiblePersonEmail: null,
    isActiveCobraRifMember: false,
    emailConfirmed: false,
    providerCertifications: [],
    joinedAt: '',
    crisisProviderCompanyAssignments: [],
    crisisProviderCountryAssignments: [],
    registrationSource: '',
    hpiEligibilityStatus: false,
    hpiMemberInsurance: null,
};

interface UpdateUserInfoPayload extends UserDetails {
    showNotification?: string;
}

export const user = {
    state: defaultState,
    reducers: {
        saveUserInfo: (state: UserState, payload: Partial<UserDetails>): UserState => {
            ReactGA.set({ userId: payload.uuid });
            // dimension1 = Company
            // https://analytics.google.com/analytics/web/#/a151748554w218086626p208055203/admin/custom-dimensions/
            ReactGA.set({ dimension1: payload.company });

            return {
                ...state,
                ...payload,
            };
        },
        showInviteDependentModal: (state: UserState): UserState => {
            state.inviteDependentModalVisible = true;

            return state;
        },
        hideInviteDependentModal: (state: UserState): UserState => {
            state.inviteDependentModalVisible = false;

            return state;
        },
        addCareRecommendation: (state: UserState, payload: CareRecommendationNode): UserState => {
            const newState = {
                ...state,
                careRecommendations: [payload, ...state.careRecommendations],
            };

            return newState;
        },
        'authentication/logout': (): UserState => {
            return defaultState;
        },
    },
    effects: dispatch => ({
        async fetchUserInfo(): Promise<UserDetails> {
            const response = await userService.fetchUserInfo();

            const { sessionInfo, userDetails } = response;

            dispatch.user.saveUserInfo(userDetails);
            Sentry.configureScope(scope => {
                scope.setUser({ id: `${userDetails.id}` });
            });

            dispatch.videoSessions.addSessionUtilizationInfo(sessionInfo);

            const companyAdditionalBenefits = await userService.fetchCompanyAdditionalBenefits();

            if (companyAdditionalBenefits) {
                dispatch.user.saveUserInfo({ companyAdditionalBenefits });
            }

            const crisisProviderCompanyAssignments = await userService.fetchCrisisProvidersAssignedToCompany();

            if (crisisProviderCompanyAssignments) {
                dispatch.user.saveUserInfo({ crisisProviderCompanyAssignments });
            }

            const crisisProviderCountryAssignments = await userService.fetchCrisisProvidersAssignedToCountry();

            if (crisisProviderCountryAssignments) {
                dispatch.user.saveUserInfo({ crisisProviderCountryAssignments });
            }

            const [hpiEligibilityStatus, hpiMemberInsurance] = await Promise.all([
                userService.fetchHPIEligibilityStatus(),
                userService.fetchHPIMemberInsurance(),
            ]);

            if (hpiEligibilityStatus) {
                dispatch.user.saveUserInfo({ hpiEligibilityStatus });
            }
            if (hpiMemberInsurance) {
                dispatch.user.saveUserInfo({ hpiMemberInsurance });
            }

            return userDetails;
        },
        async updateEmailAddress(emailAddress: string, state: RootState): Promise<void> {
            const userId = getUserId(state);

            const data = await userService.updateEmailAddress(emailAddress, userId);

            if (data) {
                dispatch.user.saveUserInfo(data);
            }
        },
        async updateUserInfo(payload: UpdateUserInfoPayload, state: RootState): Promise<void> {
            const id = getUserId(state);

            try {
                const data = await userService.updateUserInfo(payload, id);
                const languagePreferenceChanged =
                    payload.languagePreferences[0] != state.user.languagePreferences[0];

                if (data) {
                    dispatch.user.saveUserInfo(data);

                    if (languagePreferenceChanged) {
                        dispatch.assessments.fetchAssessments();
                        dispatch.countries.fetchCountries();
                        dispatch.languages.fetchLanguages();
                        dispatch.goalCategories.fetchGoalsCategories();
                        dispatch.meditation.fetchMeditations();
                        dispatch.providers.fetchMyProviders();

                        // delay is used to make sure the programs are not returned in previous language
                        await delay(500);

                        dispatch.newPrograms.fetchPrograms();
                    }

                    if (!payload.showNotification) {
                        return;
                    }

                    const message =
                        payload.email == data.newEmailAddressNotConfirmed
                            ? config.messages.userInfoEmailUpdated
                            : config.messages.userInfoUpdateSuccess;

                    toastNotificationsService.renderSuccessToast(message);
                }
            } catch {
                // error notification will be spawned from the service
                // additional error handling can be added here
            }
        },
    }),
};
